import { useEvent } from '@react-aria/utils';
import { pick } from 'lodash-es';
import type { TargetAndTransition } from 'motion/react';
import { AnimatePresence, motion } from 'motion/react';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FocusScope, useLocale, useModalOverlay } from 'react-aria';
import {
  UNSTABLE_ToastRegion as AriaToastRegion,
  UNSTABLE_ToastStateContext as AriaToastStateContext,
} from 'react-aria-components';
import { useOverlayTriggerState } from 'react-stately';

import { ChevronDownIcon, ChevronUpIcon } from '@infinex/ui-icons';
import {
  cn,
  styleVars,
  useIsSmallScreen,
  useMediaQuery,
} from '@infinex/ui-styles';
import { useDocumentVisibility } from '@infinex/ui-utils/react-dom';

import { ToasterContext } from './context';
import {
  MIN_HEIGHT,
  ToastPrimitive,
  springTransition,
  timeTransition,
} from './ToastPrimitive';
import { getGlobalToastQueue } from './ToastQueue';

import { Button } from '../button';

type ToasterProps = {
  /**
   * The alignment of the toaster.
   * @default 'right' for LTR languages, and 'left' for RTL languages.
   */
  align?: 'center' | 'left' | 'right';
  /**
   * The placement of the toaster.
   * @default 'bottom'
   */
  placement?: 'bottom' | 'top';
};

/**
 * The `Toaster` is a lifecycle and positioning manager for all toast
 * notifications, ensuring that they’re displayed in order and removed
 * appropriately. It acts as an “outlet”, rendering queued toasts within a
 * portal at the end of the document body.
 */
export function Toaster(props: ToasterProps) {
  const queue = getGlobalToastQueue();
  const regionRef = useRef<HTMLDivElement>(null);
  const { direction } = useLocale();
  const isSmallScreen = useIsSmallScreen();
  const [isExpandable, setIsExpandable] = useState(
    queue.visibleToasts.length > 1
  );

  const {
    placement = 'bottom',
    align = isSmallScreen ? 'center' : direction === 'rtl' ? 'left' : 'right',
  } = props;

  const state = useOverlayTriggerState({});
  const { isOpen: isExpanded, close, toggle } = state;
  const ctx = useMemo(
    () => ({
      placement,
      isExpanded,
      toggleExpanded() {
        if (!isExpanded && queue.visibleToasts.length <= 1) {
          return;
        }

        toggle();
      },
    }),
    [isExpanded, placement, queue, toggle]
  );

  useDocumentVisibility({ onHidden: () => queue.pauseAll() });

  useEffect(() => {
    return queue.subscribe(() => {
      // collapse the stack when the queue is emptied
      if (queue.visibleToasts.length <= 1 && isExpanded) {
        close();
      }

      // need re-render to add/remove the expand button
      setIsExpandable(queue.visibleToasts.length > 1);
    });
  }, [queue, isExpanded, close]);

  const collapse = () => {
    regionRef.current?.focus();
    ctx.toggleExpanded();
  };

  // Prevent scroll, aria hide outside, and contain focus when expanded, since
  // we take over the whole screen.
  // Attach event handler to the ref since `ToastRegion` doesn't pass through
  // "onKeyDown" prop.
  useModalOverlay({}, state, regionRef);
  useEvent(
    regionRef,
    'keydown',
    isExpanded
      ? (e) => {
          if (e.key === 'Escape') {
            collapse();
          }
        }
      : undefined
  );
  const ExpandIcon = placement === 'top' ? ChevronDownIcon : ChevronUpIcon;

  return (
    <AriaToastRegion
      ref={regionRef}
      queue={queue}
      className={cn(
        'z-toast fixed inset-x-auto flex max-h-screen flex-col gap-2 outline-hidden',
        {
          'bottom-0': placement === 'bottom',
          'top-0': placement === 'top',
        },
        {
          'left-1/2 -translate-x-1/2': align === 'center',
          'right-(--offset-right)': align === 'right',
          'left-(--offset-left)': align === 'left',
        }
      )}
      style={styleVars({
        width: 'min(420px, 90vw)',
        '--offset': isSmallScreen ? '8px' : '16px',
        '--offset-bottom': 'max(var(--offset), env(safe-area-inset-bottom))',
        '--offset-top': 'max(var(--offset), env(safe-area-inset-top))',
        '--offset-left':
          'calc(max(var(--offset), env(safe-area-inset-left)) - 4px)',
        '--offset-right':
          'calc(max(var(--offset), env(safe-area-inset-right)) - 4px)',
      })}
    >
      <FocusScope contain={isExpanded}>
        {isExpanded && (
          // clicking the background should collapse the stack. keyboard
          // behaviour handled by the button below
          // eslint-disable-next-line
          <div className="fixed inset-0" onClick={collapse} />
        )}
        {isExpandable && (
          <div
            className={cn('absolute -translate-x-full', {
              'bottom-0 pb-(--offset-bottom)': placement === 'bottom',
              'top-0 pt-(--offset-top)': placement === 'top',
            })}
          >
            <Button
              onPress={toggle}
              aria-label="Show all"
              aria-pressed={isExpanded}
              // visible to keyboard users only, when focused
              className="pointer-events-none opacity-0 focus-visible:opacity-100"
              size="small"
            >
              <ExpandIcon className={cn({ 'rotate-180': isExpanded })} />
            </Button>
          </div>
        )}

        <ToasterContext.Provider value={ctx}>
          <ToastList placement={placement} align={align} />
        </ToasterContext.Provider>
      </FocusScope>
    </AriaToastRegion>
  );
}

function ToastList(props: {
  placement: 'top' | 'bottom';
  align: 'center' | 'left' | 'right';
}) {
  const { placement } = props;

  const { isExpanded, toggleExpanded } = useContext(ToasterContext)!;
  const reduceMotion = useMediaQuery('(prefers-reduced-motion)');
  const state = useContext(AriaToastStateContext)!;
  const listRef = useRef<HTMLOListElement>(null);

  useEvent(listRef, 'click', (e) => {
    // check if the target is a button because stopPropagation in react events
    // doesn't affect native events
    if (!isExpanded && !(e.target as Element)?.closest('button')) {
      toggleExpanded();
    }
  });

  function getPerspectiveOrigin({ isHovered }: { isHovered: boolean }) {
    const origin = isHovered ? 95 : 55;
    return (
      'center ' +
      (placement === 'top' ? `calc(100% + ${origin}px)` : `${-origin}px`)
    );
  }

  const pickMotion = useCallback(
    (styles: TargetAndTransition) =>
      reduceMotion ? pick(styles, ['opacity']) : styles,
    [reduceMotion]
  );

  return (
    <motion.ol
      ref={listRef}
      whileHover={{
        perspectiveOrigin: getPerspectiveOrigin({ isHovered: true }),
      }}
      style={{
        perspective: MIN_HEIGHT,
        perspectiveOrigin: getPerspectiveOrigin({ isHovered: false }),
      }}
      transition={timeTransition}
      className={cn(
        'flex min-h-0 grow flex-col p-1', // min-padding so the focus ring isn't clipped
        {
          'gap-y-2 overflow-auto': isExpanded,
        },
        {
          'flex-col-reverse pb-(--offset-bottom)': placement === 'bottom',
          'pt-(--offset-top)': placement === 'top',
        }
      )}
    >
      <AnimatePresence mode="popLayout">
        {state.visibleToasts.map((toast, index) => {
          return (
            <motion.li
              key={toast.key}
              layout
              className={cn('shrink-0')}
              initial={pickMotion({
                opacity: 0.25,
                y: placement === 'top' ? -MIN_HEIGHT : MIN_HEIGHT,
              })}
              animate={pickMotion({ opacity: 1, y: 0 })}
              exit={pickMotion({ opacity: 0, scale: 0.6 })}
              transition={springTransition}
              style={{
                translate: isExpanded ? '0 0 0' : `0 0 ${-12 * index}px`,
                zIndex: index === 0 ? 1 : 1 - index,
              }}
            >
              <ToastPrimitive reduceMotion={reduceMotion} toast={toast} />
            </motion.li>
          );
        })}
      </AnimatePresence>
    </motion.ol>
  );
}
